// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Injectable, OnDestroy } from '@angular/core';
import { CanActivate } from '@angular/router';
import { UserPermissionIds, UserTypes } from '@interfaces/user.interfaces';
import { UserService } from '@services/user.service';
import { NavService } from '@services/nav.service';
import { MatDialog } from '@angular/material/dialog';
import { ErrorModalComponent } from '../../../modals/error-modal/error-modal.component';
import { RouteGuardService } from '@services/route-guard.service';
import { SettingsService } from '@services/settings.service';
import { Dealer } from '@interfaces/dealer.interfaces';
import { DealershipService } from '@services/dealership.service';
import { ContentService } from '@services/content.service';
import { takeWhile } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class BasePermissionGuard implements OnDestroy, CanActivate {
    userTypes: UserTypes[] = [];
    permissions: UserPermissionIds[] = [];
    private isComponentAlive: boolean = true;
    private dealer: Dealer;

    constructor(
        private readonly userService: UserService,
        private readonly navService: NavService,
        private readonly dialog: MatDialog,
        private readonly routeGuardService: RouteGuardService,
        private readonly dealershipService: DealershipService,
        private readonly contentService: ContentService,
        readonly settingsService: SettingsService
    ) {
        this.dealershipService.dealer$.subscribe((dealer) => {
            this.dealer = dealer;
        });
    }

    async canActivate(): Promise<boolean> {
        const hasAccess = this.userService.hasPermission$(this.userTypes, this.permissions);

        if (!hasAccess) {
            this.contentService
                .permissionsMissingError(this.dealer.oemCode)
                .pipe(takeWhile(() => this.isComponentAlive))
                .subscribe((message) => {
                    this.dialog
                        .open(ErrorModalComponent, {
                            width: '700px',
                            data: {
                                title: 'Access Restricted',
                                message,
                            },
                        })
                        .afterClosed()
                        .subscribe(() => {
                            this.navService.sendToHome();
                            return false;
                        });
                });
        }

        this.routeGuardService.logRouteGuard(this.constructor.name, hasAccess);
        return hasAccess;
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }
}
